/* Only an explicit preview action creates this short-lived login continuation. */
(() => {
  if (globalThis.HeirloomPreviewRequest) return;
  const key = '3dh-explicit-preview-intent-v1';
  const running = new Map();
  const server = () => globalThis.HeirloomPersonalizationServer;
  const match = (row, intent) => intent.requestId ? row.requestId === intent.requestId : row.designId === intent.designId;
  const clear = intent => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (saved && match(saved, intent)) sessionStorage.removeItem(key);
    } catch { /* The current page can still retry. */ }
  };
  const perform = async (intent, { resume = false, trigger = null } = {}) => {
    const service = server();
    if (!service) throw new Error('Design saving could not be loaded. Refresh and try again.');
    let data;
    try { data = await service.request('/v1/customer-previews'); }
    catch (error) {
      if (error.status !== 401 || resume) throw error;
      const cart = new URL('/cart', location.origin);
      const gate = globalThis.HeirloomCartUpload?.loginGate(cart.href, { verify: async () => {
        const result = await service.request('/v1/customer-previews');
        return Array.isArray(result.previews);
      } });
      if (!gate) throw new Error('Sign-in could not be opened. Refresh and try again. Your details are saved in your cart.');
      if (gate.wait) {
        const output = trigger?.parentElement.querySelector('[data-free-preview-status]');
        let cancel;
        if (output) {
          cancel = document.createElement('button');
          cancel.type = 'button';
          cancel.textContent = 'Cancel sign-in';
          cancel.addEventListener('click', () => gate.close());
          output.after(cancel);
        }
        try {
          if (!await gate.wait) { clear(intent); throw new Error('Sign-in was not completed. Your details are saved in your cart. Try the free preview again.'); }
        } finally { cancel?.remove(); }
      } else {
        let saved = false;
        try { sessionStorage.setItem(key, JSON.stringify(intent)); saved = true; } catch { /* Do not leave without a recoverable intent. */ }
        if (!saved) throw new Error('This browser could not save the sign-in step. Allow browser storage and try again. Your details are in your cart.');
        location.assign(gate.loginUrl);
        const error = new Error('Opening sign-in. Your details are saved in your cart.');
        error.code = 'PREVIEW_LOGIN_REDIRECT';
        throw error;
      }
      data = await service.request('/v1/customer-previews');
    }
    if (!Array.isArray(data.previews)) throw new Error('Your saved customization could not be checked. Try again from your cart.');
    document.dispatchEvent(new CustomEvent('heirloom:customer-authenticated'));
    const receipt = service.pending().find(saved => match(saved, intent));
    if (receipt) await service.claimCurrent(receipt);
    const requestId = intent.requestId || receipt?.requestId;
    const row = requestId
      ? (await service.request(`/v1/customer-previews/${encodeURIComponent(requestId)}`)).preview
      : data.previews.find(saved => match(saved, intent));
    if (!row?.requestId || !match(row, intent)) throw new Error('This customization could not be linked to your signed-in account. Your cart is saved. Try again or contact our team.');
    if (row.paid === true) throw new Error('This item has already been ordered. Please check your order or contact us about its design.');
    if (row.checkoutAllowed === false) throw new Error('This saved customization is no longer available for a new preview request. Please contact us for help.');
    const result = row.previewRequested === true ? { preview: row } : await service.request(`/v1/customer-previews/${encodeURIComponent(row.requestId)}/request`, { method: 'POST' });
    if (result.preview?.requestId !== row.requestId || result.preview.previewRequested !== true) throw new Error('Your preview request could not be confirmed. Try again from your cart.');
    clear(intent);
    window.dispatchEvent(new CustomEvent('heirloom:preview-request-complete', { detail: result.preview }));
    void globalThis.HeirloomCartState?.sync({ resume: true, force: true });
    document.dispatchEvent(new CustomEvent('heirloom:preview-requested', { detail: { requestId: result.preview.requestId, designId: result.preview.designId, preview: result.preview } }));
    return { ok: true, preview: result.preview };
  };
  const request = ({ requestId = '', designId = '', trigger = null } = {}) => {
    if (!requestId && !designId) return Promise.reject(new Error('Save your personalization to your cart before requesting a preview.'));
    const id = requestId || designId;
    if (running.has(id)) return running.get(id);
    const intent = { requestId, designId, expiresAt: Date.now() + 30 * 60 * 1000 };
    // A reload retries this one explicit request; ordinary sign-in never creates an intent.
    try { sessionStorage.setItem(key, JSON.stringify(intent)); } catch { /* Popup login can complete in this page without storage. */ }
    const task = perform(intent, { trigger }).catch(error => {
      if (error.code !== 'PREVIEW_LOGIN_REDIRECT') clear(intent);
      throw error;
    }).finally(() => running.delete(id));
    running.set(id, task);
    return task;
  };
  const bind = (button, save) => {
    if (!button) return;
    const host = button.parentElement;
    const dialog = host.querySelector('.optional-preview-dialog');
    const output = host.querySelector('[data-free-preview-status]');
    if (!dialog) return;
    const openCart = () => {
      if (globalThis.HeirloomCartDrawer) globalThis.HeirloomCartDrawer.open(button, { cartChanged: true });
      else location.assign('/cart');
    };
    const applySignedIn = () => {
      const signedIn = host.dataset.previewSignedIn === 'true';
      dialog.querySelector('[data-preview-signin-copy]').hidden = signedIn;
      const memberCopy = dialog.querySelector('[data-preview-member-copy]');
      if (memberCopy) memberCopy.hidden = !signedIn;
      dialog.querySelector('[data-preview-guest-label]').hidden = signedIn;
      dialog.querySelector('[data-preview-member-label]').hidden = !signedIn;
    };
    document.addEventListener('heirloom:customer-authenticated', () => { host.dataset.previewSignedIn = 'true'; applySignedIn(); });
    button.addEventListener('click', () => {
      if (button.disabled || dialog.open) return;
      applySignedIn();
      dialog.showModal();
      button.setAttribute('aria-expanded', 'true');
    });
    dialog.addEventListener('close', () => button.setAttribute('aria-expanded', 'false'));
    dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    dialog.querySelector('[data-preview-dialog-close]').addEventListener('click', () => dialog.close());
    dialog.querySelector('[data-preview-continue]').addEventListener('click', async () => {
      if (button.disabled) return;
      dialog.close();
      button.disabled = true;
      if (output) output.textContent = '';
      let submitted = false;
      try {
        const recovery = globalThis.HeirloomCurrentDraft;
        await recovery?.ready;
        if (await recovery?.reuse()) { openCart(); return; }
        const form = button.form;
        if (!form?.reportValidity()) return;
        const submit = form.querySelector('[data-relief-submit], [data-simple-submit], [data-snowman-buy]');
        if (!submit || submit.disabled) throw new Error('Complete your personalization before adding to cart.');
        // Crystal buttons also guide incomplete personalization before becoming submit buttons.
        button.disabled = false;
        submitted = true;
        submit.click();
      } catch (error) {
        if (output) output.textContent = error.message || 'Your cart could not be opened. Please try again.';
      } finally { if (!submitted) button.disabled = false; }
    });
    dialog.querySelector('[data-preview-confirm]').addEventListener('click', async () => {
      if (button.disabled) return;
      dialog.close();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (output) output.textContent = 'Saving your personalization…';
      try {
        const recovery = globalThis.HeirloomCurrentDraft;
        await recovery?.ready;
        const saved = await recovery?.reuse() || await save();
        if (saved?.serverError) throw new Error('Your cart is saved, but the design backup could not be confirmed. Review your cart before requesting a preview.');
        if (!saved) throw new Error('Complete your personalization before requesting a free preview.');
        if (output) output.textContent = 'Submitting your preview request. Sign in if prompted to save and view your design.';
        await request({ requestId: saved.requestId || saved.serverPending?.requestId || saved.serverPending?.draftReceipt?.requestId || saved.draftReceipt?.requestId || saved.expected?.draftReceipt?.requestId || '', designId: saved.id || saved.expected?.id || '', trigger: button });
        if (output) output.textContent = 'Request received. Your personalized gift is saved in your cart.';
        openCart();
      } catch (error) {
        if (output) output.textContent = error.message || 'Your preview request could not be confirmed. Try again from your cart.';
      } finally { button.disabled = false; button.removeAttribute('aria-busy'); }
    });
  };
  globalThis.HeirloomPreviewRequest = { request, bind };
  let resuming = false;
  const resume = async () => {
    if (resuming || new URL(location.href).searchParams.has('heirloom_auth_return')) return;
    let intent;
    try { intent = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { return; }
    if (!intent || !intent.expiresAt || intent.expiresAt <= Date.now()) { if (intent) clear(intent); return; }
    resuming = true;
    try {
      await perform(intent, { resume: true });
      globalThis.HeirloomCartDrawer?.open(null, { cartChanged: true });
    }
    catch (error) {
      if (error.status === 401) return;
      clear(intent);
      const host = document.querySelector('[data-simple-cart]') || document.querySelector('main');
      if (!host) return;
      let notice = host.querySelector('[data-free-preview-resume-status]');
      if (!notice) { notice = document.createElement('p'); notice.dataset.freePreviewResumeStatus = ''; notice.setAttribute('role', 'alert'); host.prepend(notice); }
      notice.textContent = error.message || 'Your preview request could not be confirmed. Try again using the free preview action for this item.';
      document.dispatchEvent(new CustomEvent('heirloom:preview-request-failed', { detail: { ...intent, message: notice.textContent } }));
    }
    finally { resuming = false; }
  };
  globalThis.addEventListener('pageshow', resume);
  void resume();
})();
